import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import BrandHero from '../components/brand/BrandHero';
import BrandBadge from '../components/brand/BrandBadge';
import { applyServerFieldErrors, formatApiError } from '../utils/errors';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const { register, handleSubmit, setError, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setFormError(null);
    try {
      await login(data.email, data.password);
      toast.success('Welcome back!');
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const applied = applyServerFieldErrors(err, setError, { fields: ['email', 'password'] });
      if (!applied.length) {
        setFormError(formatApiError(err, 'We could not sign you in. Check your email and password.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-slate-950">
      <BrandHero
        title="Hire faster with confidence."
        subtitle="Centralized hiring operations for Genesis Creations with consistent workflows, role-based control, and insight-driven decisions."
      />

      <div className="flex items-center justify-center px-4 py-8 sm:py-10 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="text-center mb-5 sm:mb-6 lg:hidden">
            <BrandBadge />
          </div>

          <div className="surface-elevated p-8" data-testid="admin-login">
            <h2 className="text-2xl font-semibold text-slate-900 mb-1">Sign in to your account</h2>
            <p className="text-sm text-slate-500 mb-6">Welcome back. Continue to Genesis Talent Hub.</p>

            {formError && (
              <div
                role="alert"
                data-testid="admin-login-error"
                className="mb-4 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3.5"
              >
                <p className="text-sm font-semibold text-danger-800">We couldn&apos;t sign you in</p>
                <p className="mt-1 text-sm text-danger-700">{formError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="admin-login-form">
              <Input
                label="Email"
                type="email"
                placeholder="you@genesiscreations.co"
                autoComplete="email"
                data-testid="admin-login-email"
                error={errors.email?.message}
                {...register('email', { required: 'Email is required' })}
              />
              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                data-testid="admin-login-password"
                error={errors.password?.message}
                {...register('password', { required: 'Password is required' })}
              />
              <div className="flex justify-end -mt-1">
                <Link to="/admin/forgot-password" className="text-sm text-brand-600 hover:underline">Forgot password?</Link>
              </div>
              <Button type="submit" loading={loading} className="w-full mt-2" data-testid="admin-login-submit">
                Sign In
              </Button>
            </form>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Applicant? <a href="/apply" className="text-brand-600 hover:underline">Apply here</a>
            {' | '}
            <a href="/track" className="text-brand-600 hover:underline">Track application</a>
          </p>
        </div>
      </div>
    </div>
  );
}
